import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  FileText, 
  BarChart3, 
  ListChecks, 
  CheckCircle2,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PlanoAcao } from '@/types/plano-acao';
import { TabDadosGerais } from './tabs/TabDadosGerais';
import { TabAnalise } from './tabs/TabAnalise';
import { TabAcoes } from './tabs/TabAcoes';
import { TabEncerramento } from './tabs/TabEncerramento';
import { usePlanoAcao } from '@/hooks/usePlanoAcao';

interface PlanoAcaoModalEnhancedProps {
  isOpen: boolean;
  onClose: () => void;
  planoExistente?: PlanoAcao;
  campoRespostaId?: string;
  respostaId: string;
  campoId: string;
  tituloSugestao?: string;
  justificativaSugestao?: string;
}

export function PlanoAcaoModalEnhanced({
  isOpen,
  onClose,
  planoExistente,
  campoRespostaId,
  respostaId,
  campoId,
  tituloSugestao,
  justificativaSugestao,
}: PlanoAcaoModalEnhancedProps) {
  const [activeTab, setActiveTab] = useState('dados');
  const isEditMode = !!planoExistente?.id;
  const isConcluido = planoExistente?.status === 'concluido';

  // Reset tab when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab('dados');
    }
  }, [isOpen]);

  const handleSuccess = () => {
    onClose();
  };

  const getStatusBadge = () => {
    if (!planoExistente) return null;
    
    const statusColors: Record<string, string> = {
      aberto: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      em_andamento: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      concluido: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      cancelado: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
    };
    
    const statusLabel = planoExistente.status.replace('_', ' ').toUpperCase();
    
    return (
      <Badge className={statusColors[planoExistente.status] || statusColors.aberto}>
        {statusLabel}
      </Badge>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between pr-8">
            <DialogTitle className="text-xl font-bold">
              {isEditMode ? 'Editar Plano de Ação' : 'Novo Plano de Ação'}
            </DialogTitle>
            {getStatusBadge()}
          </div>
          {planoExistente?.titulo && (
            <p className="text-sm text-muted-foreground mt-1 truncate">
              {planoExistente.titulo}
            </p>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          <Tabs 
            value={activeTab} 
            onValueChange={setActiveTab}
            className="flex-1 flex flex-col overflow-hidden"
          >
            <TabsList className={`grid w-full flex-shrink-0 ${isEditMode ? 'grid-cols-4' : 'grid-cols-1'}`}>
              <TabsTrigger value="dados" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                <span className="hidden sm:inline">Dados Gerais</span>
                <span className="sm:hidden">Dados</span>
              </TabsTrigger>
              
              {isEditMode && (
                <>
                  <TabsTrigger value="analise" className="flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    <span className="hidden sm:inline">Análise</span>
                    <span className="sm:hidden">GUT/A3</span>
                  </TabsTrigger>
                  
                  <TabsTrigger value="acoes" className="flex items-center gap-2">
                    <ListChecks className="h-4 w-4" />
                    <span className="hidden sm:inline">Ações 5W2H</span>
                    <span className="sm:hidden">Ações</span>
                  </TabsTrigger>
                  
                  <TabsTrigger value="encerramento" className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    <span className="hidden sm:inline">Encerramento</span>
                    <span className="sm:hidden">Verificar</span>
                  </TabsTrigger>
                </>
              )}
            </TabsList>

            <div className="flex-1 overflow-y-auto mt-4 pr-2">
              <TabsContent value="dados" className="m-0 h-full">
                <TabDadosGerais
                  planoExistente={planoExistente}
                  campoRespostaId={campoRespostaId}
                  respostaId={respostaId}
                  campoId={campoId}
                  tituloSugestao={tituloSugestao}
                  justificativaSugestao={justificativaSugestao}
                  onSuccess={handleSuccess}
                  onClose={onClose}
                />
              </TabsContent>

              {isEditMode && planoExistente && (
                <>
                  <TabsContent value="analise" className="m-0 h-full">
                    <TabAnalise planoAcaoId={planoExistente.id} readOnly={isConcluido} />
                  </TabsContent>

                  <TabsContent value="acoes" className="m-0 h-full">
                    <TabAcoes planoAcaoId={planoExistente.id} readOnly={isConcluido} />
                  </TabsContent>

                  <TabsContent value="encerramento" className="m-0 h-full">
                    <TabEncerramento planoAcaoId={planoExistente.id} readOnly={isConcluido} />
                  </TabsContent>
                </>
              )}
            </div>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}
